







import { defineComponent, computed, ref, useContext, onBeforeMount, useRoute } from '@nuxtjs/composition-api'
import { PropType } from 'vue'
import { ProductDetailsQuery, useApi, useConfig } from '~/composables';
import { Product } from '~/utils/types/Product';
import { useCart } from '~/modules/checkout/composables/useCart';
import { BUNDLE_TYPENAME } from '~/utils/constants/product';
import { merge, isEmpty } from 'lodash-es';
import { useUiNotification } from '~/composables/useUiNotification';
import getProductsBaseData from "~/modules/catalog/product/queries/getProductsBaseDataForAddToCart.gql";
import axios from 'axios';
import useLoader from '~/composables/useLoader';
import { composeQueryString } from '~/utils/helpers/cart';
import { getUserIsLogged } from '~/helpers/user/user';
import { getCustomAttributesForProductEntity } from '~/sole-api/helpers';

/**
 * This component is used as route component to manage the add to cart from other sites.
 * This component  can be used as HIDDEN component to manage the add to cart of multiple skus, like the
 * compose your subscription functionality. In this case type the "canAddToCart"" is ignored". In this case 
 * thhius component MUST be wrapped in a v-if until the complete datas needed to add the products in cart are ready and from-route prop must be true.
 * If the product prop is populated this component will manage ONLY ONE PRODUCT.
 * 
 PROPS RULES
 * product is mandatory if get-product-before is false or not set
 * product-configuration : mandatory when product is configurable to indicate which configurations are selected for adding the product to cart
 * bundleOptions : used only for bundles, it contains the bundle options as { <sku> : [bundleoptions]}
 * addedCallback : function called after the success of add to cart function but (if avoidRedirect is false) before the redirect to checkout 
 * bgClass : string containing the classes to apply to the button : note, if it contains a background color class, CHECK the text color too!
 * label: label used for the button
 * addingLabel : label used for the button when the add to cart function is loading (like "adding to cart")
 * avoidRedirect : boolean: if true (default) the page will be redirected to the checkout after the product is successfully added to cart
 * getProductBefore: boolean : if true the prop skus MUST be set. It allows to get all the product iformations needed to add it/them to cart
 * skus : array of skus to add to cart
 * forceDisable :  boolean, it force the button to be disabled
 * modulesSkus: used for bundles. Object like { <sku> : [module skus], <sku>: [module skus]} for each sku in skus prop 
 *              it contains (if needed ) the sub modules choosen. In bundles it contains the choosen products of teh  verticals option.
 *              When adding to cart from route, there will be just one element {sku : [modules skus]} retrivied in url by the route (see /routes.js)
 * fromRoute : true if the add to cart is coming from route OR if we have to trigger the add to cart from eXternal logics such as ComposeYourSubscriptionModules.
 *              In ComposeYourSubscriptionModules we have to choose all the products to add and only at the end trigger the add to cart, so with this flag we can
 *              trigger the adding (even for multiple skus) only when the user is ready.
 * forceGetConfigs : true when we have to add a configurable with tipo_abbonamento product to cart. This allows to hget all its configurations before adding the right one to cart. This covers-up a bug in the configurable products graphql (when inserted more configuration it returns only the infos for the last one in the array)
 * tipoAbbonamento : used to manage the tipo_abbonamento option separating its logic from other options in order to manage it from route
 *                   and (plus) to allow readability of the code in case of maintenance
 * tipoConsegna : used to manage the tipo_consegna option separating its logic from other options in order to manage it from route
 *                   and (plus) to allow readability of the code in case of maintenance
 * customOptions: used to add special products as quotidiano del sabato or quotidiano della domenica, usually they are bound to the ONESHOTD_MOBILE product. (To work, they need the copyDate param.)
 * trackingFunction : optional function used to track the add to cart action. It triggers before te real adding to cart to track the user intent (not the successful adding, this allows to match any difference between the adding to cart clicks from the successfull ones)
 */

export default defineComponent({
    props: {
        product: {
            type: Object as PropType<Product>,
        },
        qty: {
            type: Number,
            default: 1
        },
        /** object like  {configurable_option.attribute_id : option.uid} for example for natura : {"<sku value>": {"Mjg3":"Y29uZmlndXJhYmxlLzI4Ny8x"}} */
        /** Used for all types except bundle */
        productConfiguration: {
            type: Object as PropType<{ [k: string]: { [key: string]: string } }> /** { <sku> : { string : string}} */
        },
        /** used ONLY for bundles */
        bundleOptions: {
            type: Object as PropType<{ [key: string]: string[] }>
        },
        addedCallback: {
            type: Function
        },
        bgClass: {
            type: String
        },
        label: {
            type: String
        },
        addingLabel: {
            type: String
        },
        avoidRedirect: {
            type: Boolean,
            default: false
        },
        getProductBefore: {
            type: Boolean
        },
        skus: { //also got from route params to allow retrocompatibility
            type: Array as PropType<String[]>
        },
        forceDisable: {
            type: Boolean,
            default: false
        },
        modulesSkus: { //also got from route params to allow retrocompatibility
            type: Object as PropType<{ [key: string]: String[] }>,
            default: () => { return {} }
        },
        fromRoute: { //always true if component called from route to allow retrocompatibility
            type: Boolean,
            default: false
        },
        forceGetConfigs: { //use to force the tipo_abbonamento configs
            type: Boolean,
            default: false
        },
        tipoAbbonamento: { // {sku : mensile | trimestrale | annuale (also got from route params to allow retro-compatibility)}
            type: Object as PropType<{ [key: string]: String }>
        },
        customOptions: {
            type: Array as PropType<{ title: string; uid: string; option_id: number }[]>,
        },
        tipoConsegna: { // {sku : } posta | edicola | noConsegna (also got from route params to allow retro-compatibility)}
            type: Object as PropType<{ [key: string]: String }>
        },
        trackingFunction: {
            type: Function
        },
        utmCampaign: { //also got from route params to allow retrocompatibility
            type: String
        },
        useExistingCart: { //also got from route params to allow retrocompatibility
            type: Boolean,
            default: false
        }

    },
    setup(props, context) {
        // let requiredModules = props.skus && props.modulesSkus ? props.modulesSkus[props.skus[0].toString()] : []

        const avoidRedirect = ref<Boolean>(props.avoidRedirect);
        const { error: nuxtError, app, app: { i18n, $cookies } } = useContext();
        const { isLoading, setIsLoading} = useLoader();
        const { config: storeConfig } = useConfig();
        const storeCode = storeConfig.value.store_code;

        /** Props mutual existence validation */
        if ((props.getProductBefore && (!props.skus || props.skus.length < 1)) || !props.getProductBefore && !props.product) {
            console.error('components/AddToCart.vue component : invalid props. If product is not defined, you MUST define sku and getProductBefore');
            nuxtError({ statusCode: 500 });
        }
        const { addItem, error: cartNotBundleError, loading: isCartLoading, canAddToCart, clear: removeCart, load: loadCart } = useCart();
        const { send: sendNotification } = useUiNotification();
        const isAdding = ref<Boolean>(false);
        const customErrors = ref<Object>({});
        const cartError = computed(() => {
            return merge({}, cartNotBundleError, customErrors);
        })
        const route = useRoute();

        const getProductConfiguration = product => {
            if (props.tipoAbbonamento && props.tipoAbbonamento[product.sku]) {
                let conf = {};
                try {
                    product.configurable_options.forEach(el => {
                    if(el.attribute_code==="tipo_abbonamento" && props.tipoAbbonamento && props.tipoAbbonamento[product.sku]){
                        const abbonamentoLabel = props.tipoAbbonamento[product.sku].charAt(0).toUpperCase() + props.tipoAbbonamento[product.sku].slice(1);
                        const abbonamento = el.values.find(el2 => el2.label == abbonamentoLabel);
                        return conf[el.attribute_id] = abbonamento.uid
                    }else if(el.attribute_code==="tipo_consegna" && props.tipoConsegna && props.tipoConsegna[product.sku]){
                        const tipoConsegna = el.values.find((e) => e.label.toLowerCase() == props.tipoConsegna[product.sku].toLowerCase());
                        if(tipoConsegna){
                            return conf[el.attribute_uid] = tipoConsegna.uid
                        }else{
                            console.error('Configuration not found', props.tipoConsegna);
                            return conf;
                        }
                    }else{
                        const uid = props.productConfiguration[product.sku] && 
                            props.productConfiguration.hasOwnProperty(product.sku) &&
                            Object.values(props.productConfiguration[product.sku]).length > 0 ? 
                            Object.values(props.productConfiguration[product.sku])[0] :
                            null;
                        if(uid){
                            return conf[el.attribute_uid] = uid
                        }
                        return conf[el.attribute_uid] = el.values[0].uid
                     }
                    })
                } catch (e) {
                    console.error('Configuration not found', e)
                }
                return conf;
            }

            /** Retrocompatibility for questionIndexValue, i guess it's not used anymore, btw better handle it. */
            if (props.fromRoute && route.value.query.questionIndexValue) {
              let conf = {};
              try {
                product.configurable_options.forEach(el => {
                  if (el.attribute_code == 'taglio_quesiti') {
                    const findIndex = el.values.find(val => val.value_index == route.value.query.questionIndexValue);
                    conf[findIndex.uid] = findIndex.uid;
                  }
                })
              } catch(e) {
                console.log('Index not found', e)
              }

              return conf;
            }

            return props.productConfiguration[product.sku];
        }

        const addedErrorCallback = () => {
            sendNotification({
                id: Symbol('add_to_cart_error'),
                message: i18n.t('Error adding the product in cart') as string,
                persist: true,
                type: 'danger',
                icon: 'cross',
            });
        }
        const redirectToCheckout = (cartId)=>{
            if(!avoidRedirect.value){
                    window.location.href = `${app.$config.paywallDomain}/checkout?quoteId=${cartId}${composeQueryString({...route.value.query, ...(props.utmCampaign?{utm_campaign:props.utmCampaign}:{})})}`
                }
        }
        const addCampaignCode = async (cartId)=>{
            const body = {
                storeCode: storeConfig.value.store_code,
                datas: {
                    cartId,
                    campaign_code: route.value.query.utm_campaign,
                    utm_source: route.value.query.utm_source,
                    utm_medium: route.value.query.utm_medium,
                    utm_name: route.value.query.utm_name,
                    utm_content: route.value.query.utm_content,
                    utm_term: route.value.query.utm_term,
                    rurl: route.value.query.rurl,
                    errurl: route.value.query.errurl,
                    optGift: route.value.query.optGift,
                }
            }
            try{
                await axios.post('/sole-api/cart/setAdditionalDatas',body);
                console.log('Additional datas set : '+JSON.stringify(body))
            }catch(e){
                console.error('Error setting additional datas', e)
            }
        }
        const addedToCartCallback = async (cartId) => {
            if (props.addedCallback && typeof props.addedCallback == 'function') {
                await props.addedCallback(cartId);
                redirectToCheckout(cartId)
            } else {
                await addCampaignCode(cartId);
                redirectToCheckout(cartId)
            }
        }

        const addItemToCart = async (product, useExistingCart) => {
            /** Do the tracking just ONCE for each adding operation. If promise.all the tracking mjust be sent only ONCE so we can use useExistingCart as conditional param */
            if (props.trackingFunction && !useExistingCart) props.trackingFunction();
            const productToAdd = product && product.sku ? product : productForCart.value;
            isAdding.value = true;

            const isCustomOptions: boolean = props.customOptions != null
            const isCustomOptionFromRoute = customOptionsFromRoute(product); 

            const getQuestionIndexValue = () => {
                /** TODO get from slected variant! */
                return productToAdd.taglio_quesiti || productToAdd.software_anagrafiche || productToAdd.software_module
            }

            /**
             * For Sole 24 Ore requirements we MUST have a NEW cart for each adding.
             * We are using a parameter to manage the adding of multiple items in explicit and allowed requests (like composing a custom subscription)
            */

            const apiState = app.$vsf.$magento.config.state;

            if (!useExistingCart && !props.useExistingCart) {
                await loadCart({ realCart: true, newCart: true });
            }

            let cartId = apiState.getCartId();

            if (!cartId) {
                await loadCart({ realCart: true });
                cartId = apiState.getCartId();
            }

            /** Add item to cart for bundle items */
            if (productToAdd.__typename == BUNDLE_TYPENAME) {
                const consegnaUid = productToAdd.items.reduce((acc, item) => {
                    
                  const configOpt = item.options.reduce((acc1, opt) => {
                        
                    if (!opt.product.configurable_options) {
                            return acc1;
                        }

                        const configOpt = opt.product.configurable_options.find((configOpt) => {
                            return configOpt.attribute_code == 'tipo_consegna';
                        });

                        if(!isEmpty(configOpt)){
                            acc1 = configOpt;
                        }

                        return acc1;

                    }, {});

                    if(!isEmpty(configOpt)) {
                      acc = configOpt.attribute_uid
                    }

                    return acc;
                }, '');

                var skusToAdd = null;

                try{
                    if(consegnaUid){
                        skusToAdd = Object.keys(props.productConfiguration[productToAdd.sku][consegnaUid]) || [];
                    }else{
                        let extraSkus = [];
                        skusToAdd = props.modulesSkus[productToAdd.sku] || [];
                        if (props.fromRoute && props.tipoConsegna && props.tipoConsegna[productToAdd.sku] !== "noConsegna") {
                            extraSkus = productToAdd.items
                                .flatMap(item => item.options ?? [])
                                .filter(opt =>
                                opt.product?.configurable_options?.some(
                                    option => option.attribute_code === 'tipo_consegna'
                                )
                                )
                                .map(opt => opt.product.sku);
                        }
                        skusToAdd = [...skusToAdd, ...extraSkus];
                    }
                } catch {
                    
                    /**
                      * @Case - Here we can load all the paper options, because from the URL, if you specify paper/newsstand, you should pick all the options available. 
                    */

                    let extraSkus = [];

                    skusToAdd = props.modulesSkus[productToAdd.sku] || [];
                    
                    if (props.fromRoute && props.tipoConsegna && props.tipoConsegna[productToAdd.sku] !== "noConsegna") {
                      extraSkus = productToAdd.items
                        .flatMap(item => item.options ?? [])
                        .filter(opt =>
                          opt.product?.configurable_options?.some(
                            option => option.attribute_code === 'tipo_consegna'
                          )
                        )
                        .map(opt => opt.product.sku);
                    }

                    skusToAdd = [...skusToAdd, ...extraSkus];
                }

                skusToAdd = skusToAdd.reduce((accumulator: string[], sku: string) => {
                    if (sku.includes('-')) {
                        const [leftPart, rightPart] = sku.split('-');
                        if (leftPart === productToAdd.sku && rightPart) {
                            accumulator.push(rightPart);
                        } 
                    } else {
                        accumulator.push(sku);
                    }
                    return accumulator;
                }, []);

                if(skusToAdd && Array.isArray(skusToAdd) && skusToAdd.length > 0){
                    productToAdd.items = productToAdd.items.filter(el => {
                        if(el.required){
                            return true;
                        }
                        return el.options.find(el2 => {
                            return skusToAdd.includes(el2.product.sku) || props.modulesSkus[productToAdd.sku].includes(el2.product.sku)
                        })
                    });
                }


                /**
                 * @Required modules when not specified.
                 * Init modules with base configuration.
                 * If the addToCart comes from route, fill with required modules and use Set to create an array unique.
                */
                var selectedModules = props.modulesSkus[productToAdd.sku] ?? [];

                if (props.fromRoute) {
                  const requiredSkus = productToAdd.items
                    .filter(item => item.required && item.options_type == 'configurabili')
                    .flatMap(item => item.options.map(opt => opt.product.sku));
                    selectedModules = Array.from(new Set([
                      ...(props.modulesSkus[productToAdd.sku] ?? []),
                      ...requiredSkus
                  ]));
                }

                const data = await axios.post('/sole-api/cart/addBundleToCart', {
                    storeCode,
                    cartId,
                    product: productToAdd,
                    queryParams: route.value.query,
                    modulesSkus: selectedModules,
                    isLogged: getUserIsLogged($cookies),
                    tipoAbbonamento: props.tipoAbbonamento ? props.tipoAbbonamento[productToAdd.sku] : null,
                    tipoConsegna: props.tipoConsegna ? props.tipoConsegna[productToAdd.sku] : null
                }, { headers: {} })
                const errors = data['errors'];
                if (errors && errors.length > 0) {
                    customErrors['addItem'] = errors[0] as Error;
                }
            } else if (isCustomOptions || isCustomOptionFromRoute) {
                /** GQL is not able to finalize orders with customOptions for a magento known bug, let's fallback to API. */
                const getProductConfigs = isCustomOptionFromRoute ? isCustomOptionFromRoute : props.productConfiguration[productToAdd.sku];
                const data = await axios.post('/sole-api/cart/addCustomProduct', {
                    storeCode,
                    cartId,
                    product: productToAdd,
                    productConfigurations: getProductConfigs,
                    fromRoute: props.fromRoute
                }, { headers: {} })

                const errors = data['errors'];
                if (errors && errors.length > 0) {
                    customErrors['addItem'] = errors[0] as Error;
                }
            } else {
                const productConfiguration = getProductConfiguration(productToAdd);
                await addItem({ product: productToAdd, quantity: props.qty, productConfiguration });
            }

            cartError.value.value.addItem == null ? addedToCartCallback(cartId) : addedErrorCallback();
            isAdding.value = false;
            if (!props.fromRoute) context.emit('end')
        }

        const { query } = useApi();

        const productsForCart = ref<Product[]>();
        const productForCart = ref<Product>();
        onBeforeMount(async () => {
          if (props.getProductBefore) {
            let queryInput = { filter: { sku: { in: props.skus } } };
            
            const { data } = await query<ProductDetailsQuery>(
                getProductsBaseData,
                queryInput
            );
            
            if (props.fromRoute && isEmpty(data.products.items)) redirectToDetailProduct();

            productsForCart.value = merge([], data.products.items) as Product[];

            } else {
              productsForCart.value = merge([], [props.product]);
            }
            
            productForCart.value = productsForCart.value[0]; // Used to manage the component as button in view, it must have just one product, the only one setted by the product prop or the only sku passed.
            
            if (props.fromRoute) {
                setIsLoading(true);
                try {
                  await Promise.all(
                    productsForCart.value.map((el, index) => {
                        avoidRedirect.value = props.avoidRedirect || index < productsForCart.value.length - 1;
                        return addItemToCart(el, (index > 0 || props.useExistingCart))
                  }))
                  setIsLoading(false);
                } catch (e) {
                  redirectToDetailProduct();
                }
                context.emit('end');
                
            }

        })

        const customOptionsFromRoute = (product) => {
          if (!product.options || !props.fromRoute) return null;

          const customOptions = [];
          const { copyDate, dossierId, dossierTitle } = route.value.query;
          const mappings = {
            data_uscita: copyDate,
            dossierId: dossierId,
            dossierName: dossierTitle
          };

          for (const [title, value] of Object.entries(mappings)) {
            if (value) {
              const opt = product.options.find(el => el.title === title);
              if (opt) {
                customOptions.push({
                  option_id: String(opt.option_id),
                  option_value: value
                });
              }
            }
          }

          return customOptions;
        };

        const addingError = computed(() => {
            try {
                return cartError.value.value.addItem.message;
            } catch (e) {
                return false
            }
        });

        const redirectToDetailProduct = () => window.location.href = `${app.$config.paywallDomain}/detailProduct?sku=${props.skus}${composeQueryString({ ...route.value.query, ...(props.utmCampaign ? { utm_campaign: props.utmCampaign } : {} )})}`;
        /** Using computed to manage dynamic labels */
        const addToCartLabel = computed(() => props.label ?? i18n.t('Add to cart'))
        
        return {
            addItem,
            canAddToCart,
            isCartLoading,
            cartError,
            isAdding,
            addToCartLabel,
            addingToCartLabel: props.addingLabel ?? i18n.t('Adding to cart'),
            productForCart, //used for component shown in view
            productsForCart,
            addItemToCart,
            addingError,
        }
    }
})
